// The words of the live match page, kept apart from the page so they can be
// tested without rendering anything.
//
// Nothing here knows the rules of padel. The server replays the points and
// sends the score after each one; what is left to do on this side is to name
// things - who a side is, what a row says, how long ago a push was.

export const SIDE_US = 'TRACKED_MATCH_SIDE_US'
export const SIDE_THEM = 'TRACKED_MATCH_SIDE_THEM'

const FORMATS = {
    TRACKED_MATCH_FORMAT_ONE_SET_NINE_AD: '1 set de 9 jeux, avantage',
    TRACKED_MATCH_FORMAT_ONE_SET_NINE_NOAD: '1 set de 9 jeux, point décisif',
    TRACKED_MATCH_FORMAT_TWO_SETS_SIX_AD: '2 sets de 6 jeux, avantage',
    TRACKED_MATCH_FORMAT_TWO_SETS_SIX_NOAD: '2 sets de 6 jeux, point décisif',
}

// The same labels the apps show.
const SHOTS = {
    TRACKED_MATCH_SHOT_VIBORA: 'vibora',
    TRACKED_MATCH_SHOT_BANDERA: 'bandera',
    TRACKED_MATCH_SHOT_SMASH: 'smash',
    TRACKED_MATCH_SHOT_VOLEE: 'volée',
    TRACKED_MATCH_SHOT_AMORTIE: 'amortie',
    TRACKED_MATCH_SHOT_SERVICE: 'service',
    TRACKED_MATCH_SHOT_LOB: 'lob',
    TRACKED_MATCH_SHOT_CHIQUITA: 'chiquita',
    TRACKED_MATCH_SHOT_PASSING: 'passing',
    TRACKED_MATCH_SHOT_PAR_3: 'par 3',
    TRACKED_MATCH_SHOT_PAR_4: 'par 4',
    TRACKED_MATCH_SHOT_AUTRE: 'autre coup',
}

export function formatLabel(format) {
    return FORMATS[format] || ''
}

// "Chloé / Inès". A side nobody named is called by its place on the
// scoreboard rather than by "nous" and "eux": whoever opens the link may be
// cheering for either.
export function teamName(match, side) {
    const players = (side === SIDE_US ? match.us : match.them) || []
    const names = players.map(p => (p.name || '').trim()).filter(n => n.length > 0)

    if (names.length > 0) return names.join(' / ')

    return side === SIDE_US ? 'Équipe 1' : 'Équipe 2'
}

// A player of a side, by the 1-based index the points carry. Empty when the
// index names nobody.
export function playerName(match, side, index) {
    const players = (side === SIDE_US ? match.us : match.them) || []
    const player = players[index - 1]

    return player && player.name ? player.name.trim() : ''
}

function setsWon(after) {
    return (after.sets_us || 0) + (after.sets_them || 0)
}

function gamesPlayed(after) {
    return (after.games_us || 0) + (after.games_them || 0)
}

// What a row of the point list says happened, given the score after it and the
// score after the point before it (null for the first one).
//
// A point that ended a game, a set or the match says so, because that is what
// somebody glancing at the page wants to know.
export function pointHeadline(match, point, previous) {
    if (point.kind === 'TRACKED_MATCH_ENTRY_KIND_SCORE_OVERRIDE') {
        return 'Score corrigé'
    }

    const team = teamName(match, point.won_by)
    const after = point.score_after || {}
    const before = previous ? (previous.score_after || {}) : {}

    if (after.finished && !before.finished) return `Match pour ${team}`
    if (setsWon(after) > setsWon(before)) return `Set pour ${team}`
    if (gamesPlayed(after) !== gamesPlayed(before)) return `Jeu pour ${team}`

    return `Point pour ${team}`
}

// The coach's notes on a point, when the owner chose to show them: "Coup
// gagnant · Chloé · smash · au milieu". Empty when there is nothing to say.
export function pointDetails(match, point) {
    if (!match.show_details || point.kind !== 'TRACKED_MATCH_ENTRY_KIND_POINT') return ''

    const endedBy = point.ended_by
    if (endedBy !== SIDE_US && endedBy !== SIDE_THEM) return ''

    const parts = []
    const winner = endedBy === point.won_by

    if (winner) {
        parts.push('Coup gagnant')
    } else if (point.error_kind === 'TRACKED_MATCH_ERROR_KIND_UNFORCED') {
        parts.push('Faute directe')
    } else if (point.error_kind === 'TRACKED_MATCH_ERROR_KIND_FORCED') {
        parts.push('Faute provoquée')
    } else {
        parts.push('Faute')
    }

    const player = playerName(match, endedBy, point.ended_by_player)
    if (player) parts.push(player)

    const shot = SHOTS[point.shot]
    if (shot) parts.push(shot)

    if (winner) {
        const beaten = endedBy === SIDE_US ? SIDE_THEM : SIDE_US

        if (point.beat_player === 3) {
            parts.push('au milieu')
        } else {
            const name = playerName(match, beaten, point.beat_player)
            if (name) parts.push(`passe ${name}`)
        }
    }

    return parts.join(' · ')
}

// "6-4 3-5 · 40-30" - the score as it stood once the point was played. A point
// that ended a game leaves 0-0 on court, which says nothing the games do not,
// so that row is the games alone.
export function scoreLine(point) {
    const after = point.score_after || {}
    const summary = after.summary || '0-0'

    if (after.finished || !after.display_us) return summary
    if (after.display_us === '0' && after.display_them === '0') return summary

    return `${summary} · ${after.display_us}-${after.display_them}`
}

// "il y a 8 s", "il y a 3 min", "il y a 2 h".
export function sinceText(updatedAtSeconds, nowMillis) {
    const seconds = Math.max(0, Math.round(nowMillis / 1000 - Number(updatedAtSeconds)))

    if (seconds < 60) return `il y a ${seconds} s`
    if (seconds < 3600) return `il y a ${Math.floor(seconds / 60)} min`

    return `il y a ${Math.floor(seconds / 3600)} h`
}
